import { useApolloClient } from '@apollo/client/react/hooks/useApolloClient';
import { SignalIncident } from '@provision/entity-types';
import { useEffect } from 'react';
import { filter } from 'rxjs/operators';
import { wsEventBus } from 'src/core/realtime-connection';

/**
 * Keeps the timeline card current without polling: each notifier write emits an `EntityChange` the
 * Real Time Engine pushes over the WebSocket, and patching the cached entity re-renders in place.
 *
 * EntityChange `attributes` only contain the fields written in that update — resolve sends
 * status/resolvedAt/resolvedBy, the notifier sends notificationLog. Only overwrite a field when
 * the event actually carries it, or a resolve would wipe the cached log (and the history block
 * vanishes until refresh).
 */
export const useSignalIncidentLiveUpdates = (conversationId?: string) => {
  const { cache } = useApolloClient();

  useEffect(() => {
    if (!conversationId) {
      return;
    }

    const subscription = wsEventBus
      .onEntity<SignalIncident>({ name: 'SignalIncident', operation: 'update' })
      .pipe(filter((event) => event.attributes.conversationId === conversationId))
      .subscribe({
        next: (event) => {
          const id = cache.identify({
            __typename: 'SignalIncident',
            id: event.attributes.id,
          });

          if (!id) {
            return;
          }

          const { notificationLog, status, resolvedAt, resolvedBy } = event.attributes;

          cache.modify({
            id,
            fields: {
              ...(notificationLog !== undefined && {
                notificationLog: () => notificationLog,
              }),
              ...(status !== undefined && {
                status: () => status,
              }),
              ...(resolvedAt !== undefined && {
                resolvedAt: () => resolvedAt,
              }),
              ...(resolvedBy !== undefined && {
                resolvedBy: () => resolvedBy,
              }),
            },
          });
        },
      });

    return () => subscription.unsubscribe();
  }, [cache, conversationId]);
};
